import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Camera, X, Trash, UploadSimple, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const MAX_BYTES = 8 * 1024 * 1024; // 8MB — Cloudinary's free tier is fine with this
const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/heic"];

// Build a Cloudinary delivery URL with a small set of safe transformations.
// We never trust the user-controlled secure_url for transforms — we recompose
// from cloud_name + public_id so we control quality/format.
function thumbUrl(secureUrl, w = 600) {
  if (!secureUrl) return "";
  // secure_url shape: https://res.cloudinary.com/{cloud}/image/upload/v.../{public_id}.{ext}
  return secureUrl.replace(
    "/image/upload/",
    `/image/upload/c_fill,w_${w},h_${w},g_auto,q_auto,f_auto/`
  );
}

function fullUrl(secureUrl) {
  if (!secureUrl) return "";
  return secureUrl.replace(
    "/image/upload/",
    "/image/upload/w_1600,c_limit,q_auto,f_auto/"
  );
}

function UploadDialog({ open, onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [weekNum, setWeekNum] = useState("");
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setCaption("");
      setWeekNum("");
      setProgress(0);
      setBusy(false);
    }
  }, [open]);

  const pickFile = (f) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Please pick a JPG, PNG, WebP or HEIC photo");
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("Photo is bigger than 8 MB — try a smaller one");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Pick a photo first");
      return;
    }
    setBusy(true);
    setProgress(5);
    try {
      // 1. Ask backend for a fresh signature.
      const { data: sig } = await api.get("/cloudinary/signature", { params: { folder: "highlights" } });
      setProgress(15);

      // 2. Upload directly to Cloudinary using the signature. We use XHR so we
      //    get progress events; fetch() doesn't expose upload progress yet.
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      const uploadResult = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            // Map [0..1] of upload to [15..85] of dialog progress.
            setProgress(15 + Math.round((ev.loaded / ev.total) * 70));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); }
            catch (err) { reject(err); }
          } else {
            reject(new Error(`Cloudinary returned ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error("Upload network error"));
        xhr.send(form);
      });

      setProgress(90);

      // 3. Persist the highlight on our backend.
      const { data: highlight } = await api.post("/highlights", {
        public_id: uploadResult.public_id,
        secure_url: uploadResult.secure_url,
        caption,
        week_num: weekNum ? Number(weekNum) : null,
      });

      setProgress(100);
      toast.success("Highlight added");
      onUploaded(highlight);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      >
        <motion.form
          onSubmit={submit}
          onClick={(e) => e.stopPropagation()}
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          className="bg-[#FFFDF8] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          data-testid="highlight-upload-dialog"
        >
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-xs font-bold tracking-wider uppercase text-slate-500">New highlight</div>
              <h3 className="font-outfit font-bold text-xl">Share a class photo</h3>
            </div>
            <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
              <X size={18} />
            </button>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="text-sm font-medium block mb-1">Photo</label>
              {!file ? (
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  data-testid="highlight-pick-btn"
                  className="w-full border-2 border-dashed border-slate-300 rounded-2xl px-4 py-10 bg-white hover:bg-slate-50 text-slate-500 flex flex-col items-center gap-2"
                >
                  <UploadSimple size={28} weight="bold" />
                  <span className="font-semibold text-sm">Tap to choose a photo</span>
                  <span className="text-[11px]">JPG, PNG, WebP up to 8 MB</span>
                </button>
              ) : (
                <div className="bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-3">
                  <ImageSquare weight="fill" className="text-yellow-500 shrink-0" size={28} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{file.name}</div>
                    <div className="text-[11px] text-slate-500">{(file.size / 1024 / 1024).toFixed(1)} MB</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-500"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED.join(",")}
                onChange={(e) => pickFile(e.target.files?.[0])}
                data-testid="highlight-file-input"
                className="hidden"
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Caption <span className="text-slate-400 font-normal">(optional)</span></label>
              <textarea
                rows={3}
                maxLength={280}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="What's happening in this photo?"
                data-testid="highlight-caption-input"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              />
              <div className="text-[11px] text-slate-400 text-right">{caption.length}/280</div>
            </div>

            <div>
              <label className="text-sm font-medium block mb-1">Tie to a week <span className="text-slate-400 font-normal">(optional)</span></label>
              <select
                value={weekNum}
                onChange={(e) => setWeekNum(e.target.value)}
                data-testid="highlight-week-select"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
              >
                <option value="">No week — general highlight</option>
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>Week {n}</option>
                ))}
              </select>
            </div>

            {busy && (
              <div className="bg-slate-100 rounded-full h-2 overflow-hidden" data-testid="highlight-progress">
                <div
                  className="h-full bg-yellow-400 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
          <div className="p-6 border-t border-slate-200 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-3 py-2 rounded-xl hover:bg-slate-100 text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !file}
              data-testid="highlight-submit-btn"
              className="btn-tactile-yellow px-4 py-2 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500 disabled:opacity-60"
            >
              {busy ? `Uploading ${progress}%` : "Add highlight"}
            </button>
          </div>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
}

function Lightbox({ highlight, onClose }) {
  if (!highlight) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
        data-testid="highlight-lightbox"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
          <X size={24} weight="bold" />
        </button>
        <motion.div
          onClick={(e) => e.stopPropagation()}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="max-w-5xl max-h-[90vh] w-full"
        >
          <img
            src={fullUrl(highlight.secure_url)}
            alt={highlight.caption || "Class highlight"}
            className="w-full max-h-[80vh] object-contain rounded-2xl bg-black"
          />
          {(highlight.caption || highlight.week_num) && (
            <div className="mt-3 text-white">
              {highlight.week_num && (
                <span className="inline-block text-xs font-bold tracking-wider uppercase bg-yellow-400 text-yellow-950 px-2 py-0.5 rounded mr-2">
                  Week {highlight.week_num}
                </span>
              )}
              {highlight.caption && <p className="inline">{highlight.caption}</p>}
              <p className="text-xs text-white/60 mt-1">
                Shared by {highlight.uploaded_by_name} · {new Date(highlight.created_at).toLocaleDateString()}
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function HighlightsCard({ readOnly = false, preloaded = null }) {
  const { user } = useAuth();
  const canEdit = !readOnly && !!(user?.is_admin || user?.can_edit_curriculum);
  const [highlights, setHighlights] = useState(preloaded || []);
  const [loading, setLoading] = useState(!preloaded);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (preloaded) return; // parent portal feeds us via the public endpoint
    let cancelled = false;
    api
      .get("/highlights")
      .then(({ data }) => {
        if (!cancelled) setHighlights(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [preloaded]);

  const onUploaded = (h) => setHighlights((arr) => [h, ...arr]);

  const remove = async (h) => {
    if (!window.confirm(`Remove this highlight? It'll be deleted from Cloudinary too.`)) return;
    try {
      await api.delete(`/highlights/${h.id}`);
      setHighlights((arr) => arr.filter((x) => x.id !== h.id));
      toast.success("Highlight removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove highlight");
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="highlights-card">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2">
          <Camera weight="fill" className="text-pink-500" size={22} />
          <h3 className="font-outfit font-bold text-xl">Class highlights</h3>
        </div>
        {canEdit && (
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="highlights-add-btn"
            className="btn-tactile-yellow inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-yellow-400 text-yellow-950 font-bold text-sm hover:bg-yellow-500"
          >
            <UploadSimple size={14} weight="bold" /> Add photo
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm" data-testid="highlights-loading">Loading...</div>
      ) : highlights.length === 0 ? (
        <div className="text-center py-10 text-slate-400" data-testid="highlights-empty">
          <ImageSquare size={36} weight="duotone" className="mx-auto mb-2 text-slate-300" />
          <p className="text-sm">No highlights yet.</p>
          {canEdit && (
            <p className="text-xs mt-1">Tap <strong>Add photo</strong> to share the first one.</p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="highlights-grid">
          {highlights.map((h) => (
            <div
              key={h.id}
              data-testid={`highlight-${h.id}`}
              className="relative group rounded-2xl overflow-hidden bg-slate-100 aspect-square"
            >
              <button
                onClick={() => setActive(h)}
                className="absolute inset-0 w-full h-full"
                aria-label={h.caption || "Open photo"}
                data-testid={`highlight-open-${h.id}`}
              >
                <img
                  src={thumbUrl(h.secure_url, 400)}
                  alt={h.caption || "Class highlight"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </button>
              {h.week_num && (
                <span className="absolute top-2 left-2 text-[10px] font-bold tracking-wider uppercase bg-white/90 backdrop-blur px-2 py-0.5 rounded">
                  Wk {h.week_num}
                </span>
              )}
              {h.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6 text-white text-xs line-clamp-2 pointer-events-none">
                  {h.caption}
                </div>
              )}
              {canEdit && (
                <button
                  onClick={() => remove(h)}
                  data-testid={`highlight-remove-${h.id}`}
                  className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 hover:bg-red-500 hover:text-white text-slate-700 opacity-0 group-hover:opacity-100 transition"
                  title="Remove"
                >
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={onUploaded} />
      <Lightbox highlight={active} onClose={() => setActive(null)} />
    </div>
  );
}
